/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import { useState, useEffect } from "react";

/**
 * usePageVisibility - Hook to track page visibility
 * 
 * Returns true when page is visible, false when hidden (tab switched, minimized, etc.)
 * Useful for pausing polling/auto-refresh when user is not viewing the page.
 * 
 * @returns {boolean} isVisible - Whether the page is currently visible
 */
export function usePageVisibility() {
  const [isVisible, setIsVisible] = useState(() => {
    // Initial state: assume visible if document.hidden is not available
    if (typeof document === "undefined") return true;
    return !document.hidden;
  });

  useEffect(() => {
    // Browser doesn't support Page Visibility API
    if (typeof document === "undefined" || typeof document.hidden === "undefined") {
      return;
    }

    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial check
    setIsVisible(!document.hidden);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return isVisible;
}

export default usePageVisibility;
